'use client';

import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { BottomSheet } from '../ui/BottomSheet';
import { Button } from '../ui/Button';
import { cn } from '../../utils/cn';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useConfirmDialogStore } from '../../stores/confirmDialogStore';
import {
  confirmAnswerId,
  confirmButtons,
  dismissAnswer,
  isRefusalButton,
  type ConfirmButton,
} from './confirmAnswer';
import translations from './ConfirmDialog.translations';

/** BottomSheet's own exit animation. The request leaves the queue only after
 *  the panel has finished sliding out, so a queued one doesn't cut it off. */
const EXIT_MS = 340;

const variantFor = (button: ConfirmButton) => {
  if (button.kind === 'destructive') return 'destructiveSecondary' as const;
  if (isRefusalButton(button)) return 'tertiary' as const;
  return 'primary' as const;
};

/**
 * Both secondary variants fill with `bg-bg-secondary` — the sheet panel's own
 * colour. Left alone, the destructive answer and the refusal would be
 * fill-on-fill: red and white lettering floating on nothing, the same way #81
 * lost «Выйти» to a black button on a black screen. `fill-tertiary` is the
 * fill the Figma "Bordered" style calls for, and the one the sheet's own close
 * button already uses.
 */
const fillFor = (variant: ReturnType<typeof variantFor>) =>
  variant === 'primary' ? undefined : 'bg-fill-tertiary';

/**
 * The app's own confirmation, for every runtime without a Telegram popup.
 *
 * Mounted once per tree (`Providers` on web, `TmaApp` in the mini app) and fed
 * by `openConfirmDialog`; screens keep calling `useConfirmPopup()` and never
 * touch this. Drawn with our own `BottomSheet`, which already brings what a
 * money confirmation needs: a portal above the screen stack, body-scroll lock,
 * an Escape stack that only answers the topmost sheet, backdrop and swipe-down
 * closes.
 *
 * Every close that isn't a button — backdrop, Escape, swipe, the corner cross,
 * browser back — answers with refusal (`dismissAnswer`).
 */
export const ConfirmDialogHost: FC = () => {
  const { t } = useLocalTranslation(translations);
  const request = useConfirmDialogStore((s) => s.queue[0] ?? null);
  const primaryHost = useConfirmDialogStore((s) => s.hosts[0]);
  const drop = useConfirmDialogStore((s) => s.drop);
  const addHost = useConfirmDialogStore((s) => s.addHost);
  const removeHost = useConfirmDialogStore((s) => s.removeHost);

  const [token] = useState(() => Symbol('confirm-dialog-host'));
  const [answeredId, setAnsweredId] = useState<number | null>(null);

  // Callbacks below are wired to document-level listeners that must not
  // re-subscribe on every queue change, so they read the head through a ref.
  const requestRef = useRef(request);
  requestRef.current = request;
  const answeredRef = useRef<number | null>(null);
  const exitTimerRef = useRef<number | null>(null);

  useEffect(() => {
    addHost(token);
    return () => removeHost(token);
  }, [token, addHost, removeHost]);

  const answer = useCallback(
    (buttonId: string | null) => {
      const current = requestRef.current;
      if (!current || answeredRef.current === current.id) return;
      answeredRef.current = current.id;
      setAnsweredId(current.id);
      // Resolve now, unmount later: the caller's flow continues while the
      // panel is still sliding away. `window.confirm` held the whole page
      // hostage until it was answered.
      current.resolve(buttonId);
      if (exitTimerRef.current !== null) window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = window.setTimeout(() => {
        exitTimerRef.current = null;
        drop(current.id);
      }, EXIT_MS);
    },
    [drop],
  );

  const dismiss = useCallback(() => {
    const current = requestRef.current;
    if (!current) return;
    answer(dismissAnswer(current.options));
  }, [answer]);

  // Browser back. The host lives above the router, so without this the panel
  // would hang over whatever screen the user landed on, with the caller still
  // awaiting an answer. Anything queued behind belongs to the screen we just
  // left, so it goes the same way.
  useEffect(() => {
    if (primaryHost !== token) return;
    const onPopState = () => {
      const { queue, drop: dropRequest } = useConfirmDialogStore.getState();
      dismiss();
      for (const queued of queue.slice(1)) {
        queued.resolve(dismissAnswer(queued.options));
        dropRequest(queued.id);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [dismiss, primaryHost, token]);

  // Host torn down with an answer still pending (route swap, runtime switch):
  // refuse, so no caller is left awaiting a promise nobody will settle.
  useEffect(
    () => () => {
      if (exitTimerRef.current !== null) window.clearTimeout(exitTimerRef.current);
      const current = requestRef.current;
      if (current && answeredRef.current !== current.id) {
        current.resolve(dismissAnswer(current.options));
      }
    },
    [],
  );

  if (primaryHost !== token || !request) return null;

  const { title, message } = request.options;
  const buttons = confirmButtons(request.options);

  return (
    <BottomSheet
      isOpen={answeredId !== request.id}
      onClose={dismiss}
      hideHeader
      heightClass="h-auto max-h-[85vh]"
      title={title ?? message}
      closeAriaLabel={t('close')}
    >
      <div
        data-testid="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        className="flex flex-col pt-14 pb-2"
      >
        {title ? (
          <h2 className="text-label-primary text-[19px] font-semibold leading-[24px] text-center text-balance">
            {title}
          </h2>
        ) : null}
        <p className="mt-2 text-label-secondary text-[15px] leading-[20px] text-center whitespace-pre-line">
          {message}
        </p>
        <div className="mt-5 flex flex-col gap-2">
          {buttons.map((button, index) => {
            const variant = variantFor(button);
            return (
            <Button
              key={`${button.kind}-${button.id ?? index}`}
              size="l"
              stretched
              // Buttons are nowrap by default; a longer localisation must wrap
              // inside the pill instead of being clipped by it.
              className={cn('whitespace-normal', fillFor(variant))}
              variant={variant}
              onClick={() => answer(confirmAnswerId(button))}
            >
              {button.text ??
                t(button.kind === 'cancel' ? 'cancel' : button.kind === 'ok' ? 'ok' : 'close')}
            </Button>
            );
          })}
        </div>
      </div>
    </BottomSheet>
  );
};
